import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, User, RefreshCw } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { blogPosts } from "@/data/blogPosts";
import ReactMarkdown from "react-markdown";
import { SchemaMarkup, generateBreadcrumbSchema, generateHowToSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { airports } from "@/data/airports";
import { RelatedContent } from "@/components/RelatedContent";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

// HowTo schema for specific posts
const getHowToSchemaForPost = (slug: string) => {
  if (slug === "tips-for-avoiding-long-lines-at-airport-security") {
    return generateHowToSchema(
      "How to Avoid Long Lines at Airport Security",
      "15 proven strategies to skip long TSA security lines and save 30+ minutes at any airport.",
      [
        { name: "Get TSA PreCheck", text: "Apply for TSA PreCheck ($78 for 5 years) to access dedicated shorter lanes with expedited screening." },
        { name: "Check Wait Times Before Arriving", text: "Use AirportBuddy to check real-time TSA wait times and plan your arrival accordingly." },
        { name: "Fly During Off-Peak Hours", text: "Avoid peak times (5-8 AM, 11 AM-1 PM, 4-7 PM). Midweek flights typically have shorter lines." },
        { name: "Choose the Right Lane", text: "Left lanes are often shorter as most right-handed people instinctively go right." },
        { name: "Prepare Before the Line", text: "Remove belt, watch, jewelry, and empty pockets before reaching the scanner." },
        { name: "Use a TSA-Friendly Bag", text: "Organize liquids in clear bags and laptops in easily accessible compartments." },
        { name: "Wear Slip-On Shoes", text: "Avoid lace-up boots and complex footwear that slows you down at the scanner." },
        { name: "Check Alternative Checkpoints", text: "Many airports have multiple security checkpoints - some are less crowded than the main one." }
      ],
      "PT30M"
    );
  }
  return null;
};
/**
 * Extract FAQ pairs from markdown content by finding ## headings
 * that look like questions (contain "?" or start with how/what/when/why/does/can/is)
 * and using the following paragraph as the answer.
 */
const extractFAQFromContent = (content: string): { question: string; answer: string }[] => {
  const lines = content.split("\n");
  const faqs: { question: string; answer: string }[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const headingMatch = typeof line === "string" ? line.match(/^#{1,3}\s+(.+)/) : null;
    if (!headingMatch) continue;

    const heading = headingMatch[1].trim();
    const isQuestion =
      heading.includes("?") ||
      /^(how|what|when|why|does|can|is|should|do|are|will)\b/i.test(heading);

    if (!isQuestion) continue;

    // Collect the next non-empty lines as the answer (up to 3 lines)
    const answerLines: string[] = [];
    for (let j = i + 1; j < lines.length && answerLines.length < 3; j++) {
      const nextLine = lines[j].trim();
      if (nextLine.startsWith("#")) break;
      if (nextLine.length > 0 && !nextLine.startsWith("!") && !nextLine.startsWith("-")) {
        answerLines.push(nextLine);
      }
    }

    if (answerLines.length > 0) {
      faqs.push({ question: heading.replace(/\?$/, "") + "?", answer: answerLines.join(" ") });
    }
  }

  return faqs.slice(0, 8); // Max 8 FAQ items
};

/**
 * Find airports mentioned in the blog post content for cross-linking
 */
const getRelatedAirportsForPost = (content: string, keywords: string[]) => {
  const allText = (content + " " + keywords.join(" ")).toUpperCase();
  return airports
    .filter(a => allText.includes(a.code))
    .slice(0, 6);
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) return <NotFound />;

  const displayDate = post.dateModified || post.date;
  const formattedDate = new Date(displayDate).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });

  return (
    <>
      <Helmet>
        <title>{`${post.title} | AirportBuddy`}</title>
        <meta name="description" content={`✅ ${post.description}`.slice(0, 157) + (`✅ ${post.description}`.length > 157 ? "…" : "")} />
        <meta name="keywords" content={post.keywords.join(", ") + ", 2025 travel tips"} />
        <link rel="canonical" href={`https://airportbuddy.app/blog/${slug}`} />
        
        {/* Freshness signals */}
        <meta name="article:published_time" content={post.date} />
        {post.dateModified && <meta name="article:modified_time" content={post.dateModified} />}
        <meta property="og:updated_time" content={displayDate} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://airportbuddy.app/blog/${slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
      </Helmet>

      <SchemaMarkup schema={{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.description,
        "datePublished": post.date,
        "dateModified": post.dateModified || post.date,
        "author": {
          "@type": "Person",
          "name": post.author,
          "jobTitle": post.authorTitle || "Travel Expert",
          "url": "https://airportbuddy.app/about"
        },
        "publisher": {
          "@type": "Organization",
          "name": "AirportBuddy",
          "url": "https://airportbuddy.app",
          "logo": {
            "@type": "ImageObject",
            "url": "https://airportbuddy.app/logo.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://airportbuddy.app/blog/${slug}`
        }
      }} />

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${slug}` }
      ])} />

      {/* HowTo schema for applicable posts */}
      {getHowToSchemaForPost(slug || '') && (
        <SchemaMarkup schema={getHowToSchemaForPost(slug || '')!} />
      )}

      {/* FAQ schema extracted from post content */}
      {(() => {
        const faqPairs = extractFAQFromContent(post.content);
        return faqPairs.length > 0 ? <SchemaMarkup schema={generateFAQSchema(faqPairs)} /> : null;
      })()}

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6 min-h-[44px]">
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Blog
            </Link>
          </Button>

          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Blog", href: "/blog" },
            { label: post.title, href: `/blog/${slug}` }
          ]} />

          <article className="max-w-3xl mx-auto space-y-8">
            <header className="space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                {post.title}
              </h1>
              
              {/* Updated badge for freshness */}
              {post.dateModified && (
                <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Updated {formattedDate}
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-4 text-base text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4" aria-hidden="true" />
                  <span>
                    {post.author}
                    {post.authorTitle && <span className="text-xs ml-1">({post.authorTitle})</span>}
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  <time dateTime={displayDate}>
                    {formattedDate}
                  </time>
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" aria-hidden="true" />
                  {post.readTime}
                </span>
              </div>
            </header>

            <Card className="shadow-lg">
              <CardContent className="pt-6 prose prose-base md:prose-lg max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </CardContent>
            </Card>

            {/* App install CTA right after the article */}
            <AppStoreCTA
              placement="blog_post"
              context={post.slug}
              headline="Never guess how early to leave again"
              subline="The free Airport Buddy iPhone app shows live TSA waits and tells you when to head out."
            />

            {/* Related Content */}
            <RelatedContent 
              type="blog"
              currentId={post.slug}
              relatedKeywords={post.keywords.slice(0, 5)}
              maxItems={3}
            />

            {/* Airport cross-links for internal linking */}
            {(() => {
              const relatedAirports = getRelatedAirportsForPost(post.content, post.keywords);
              if (relatedAirports.length === 0) return null;
              return (
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="font-bold mb-3">Check Live Wait Times</h3>
                    <div className="flex flex-wrap gap-2">
                      {relatedAirports.map(a => (
                        <Link
                          key={a.code}
                          to={`/airports/${a.code.toLowerCase()}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors text-foreground"
                        >
                          {a.code} <span className="text-muted-foreground">→</span>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            <div className="text-center space-y-4 pt-8">
              <h2 className="text-2xl md:text-3xl font-semibold">Start Using Airport Wait Buddy Today</h2>
              <p className="text-base text-muted-foreground">
                Get real-time airport information and make your travel stress-free
              </p>
              <Button asChild size="lg" className="min-h-[48px]">
                <Link to="/auth">Sign Up Free</Link>
              </Button>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
